import { useEffect, useState } from 'react';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { recordedAmount } from '../form/amountFields';
import { GroupMember } from '../grouping';
import { moneyOrNotRecorded } from '../notRecorded';
import { LifeEventRow } from '../rows';
import { CashflowModalShell } from './modal/CashflowModalShell';
import { AmountField, allocationsComplete, sumAmounts } from './modal/MemberAllocations';
import { RemovedItem, useAllocations } from './modal/useAllocations';
import { SHARED_ITEM_NOTICE, isPartOfAGroup } from './modal/sharedItem';

const AMOUNT_FIELDS: AmountField[] = [{ key: 'cost', label: 'Amount' }];

export type LifeEventSubmission = {
  description: string;
  eventDate: string;
  details: string;
  allocations: { clientId: string; itemId?: string; cost: number | null }[];
  groupId?: string;
  removedItemIds: RemovedItem[];
};

type LifeEventModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (submission: LifeEventSubmission) => Promise<void>;
  members: GroupMember[];
  defaultClientId?: string;
  initialRow?: LifeEventRow;
  /**
   * The view behind this modal reads only one client's rows, so a shared item is open in part.
   * What every row of the group carries stays read-only — see {@link SHARED_ITEM_NOTICE}.
   */
  ownRowsOnly?: boolean;
};

export function LifeEventFormModal({
  opened,
  onClose,
  onSubmit,
  members,
  defaultClientId,
  initialRow,
  ownRowsOnly,
}: LifeEventModalProps) {
  const currency = useCurrency();
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [details, setDetails] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const seed = initialRow?.items.map(item => ({
    clientId: item.clientId,
    itemId: item.id,
    amounts: { cost: item.cost },
  }));
  const { allocations, removedItems, addMember, removeMember, setAmount } = useAllocations(
    opened,
    seed,
    defaultClientId,
  );

  useEffect(() => {
    if (!opened) return;
    setDescription(initialRow?.description ?? '');
    setEventDate(initialRow?.eventDate?.split('T')[0] ?? '');
    setDetails(initialRow?.details ?? '');
  }, [opened, initialRow]);

  const locked = Boolean(ownRowsOnly) && isPartOfAGroup(initialRow?.items);
  const total = sumAmounts(allocations, 'cost');
  const canSubmit =
    !!description.trim() && !!eventDate && allocationsComplete(allocations, AMOUNT_FIELDS);

  const handleSubmit = async () => {
    if (!canSubmit || isSaving) return;
    setIsSaving(true);
    try {
      await onSubmit({
        description,
        eventDate,
        details,
        groupId: initialRow?.items.find(item => item.groupId)?.groupId ?? undefined,
        allocations: allocations.map(allocation => ({
          clientId: allocation.clientId,
          itemId: allocation.itemId,
          cost: recordedAmount(allocation.amounts.cost),
        })),
        removedItemIds: removedItems,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CashflowModalShell
      opened={opened}
      onClose={onClose}
      title={initialRow ? 'Edit life event' : 'Add life event'}
      submitLabel={initialRow ? 'Save life event' : 'Add life event'}
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      isSaving={isSaving}
      previewTitle="Total cost preview"
      previewRows={[{ label: 'Total cost', value: moneyOrNotRecorded(total, currency) }]}
      testId="add-life-event-modal"
      fields={[
        [
          {
            kind: 'text',
            label: 'Description',
            value: description,
            onChange: setDescription,
            required: true,
            disabled: locked,
          },
          {
            kind: 'date',
            label: 'Date',
            value: eventDate,
            onChange: setEventDate,
            required: true,
            disabled: locked,
          },
        ],
        [
          {
            kind: 'text',
            label: 'Details',
            value: details,
            onChange: setDetails,
            disabled: locked,
          },
        ],
      ]}
      allocation={{
        members,
        allocations,
        amountFields: AMOUNT_FIELDS,
        emptyLabel: 'Select at least 1 family member to add a life event',
        membersLocked: locked,
        onAdd: addMember,
        onRemove: removeMember,
        onAmountChange: setAmount,
      }}
      notice={locked ? SHARED_ITEM_NOTICE : undefined}
    />
  );
}
